import { z } from "zod";
import { CompanyIdTypeSchema } from "./user";

// =============
// Billing & Subscriptions
// =============
// Note: PlanId and PlanLimits types are exported from ./plans.ts
// These schemas provide runtime validation

export const PlanIdSchema = z.enum([
	"bootstrap",
	"standard",
	"scale",
	"enterprise",
]);

// Payment method schema
export const PaymentMethodSchema = z.object({
	id: z.string(),
	brand: z.string(), // visa, mastercard, amex, etc
	last4: z.string(),
	expMonth: z.number(),
	expYear: z.number(),
	isDefault: z.boolean(),
});

// Invoice schema for subscription billing (renamed to avoid conflict with stripe.ts)
export const SubscriptionInvoiceSchema = z.object({
	id: z.string(),
	number: z.string().nullable(),
	status: z.string().nullable(), // Stripe invoice status - can be draft, open, paid, void, uncollectible, etc.
	amountDue: z.number(), // cents
	amountPaid: z.number(), // cents
	currency: z.string(),
	created: z.coerce.date(),
	periodStart: z.coerce.date(),
	periodEnd: z.coerce.date(),
	pdfUrl: z.string().nullable(),
	hostedUrl: z.string().nullable(),
});

// Tax ID schema for company identifiers
export const TaxIdSchema = z.object({
	type: z.string(), // Stripe tax ID type (us_ein, eu_vat, etc.)
	value: z.string(),
});

// Billing details schema
export const BillingDetailsSchema = z.object({
	paymentMethods: z.array(PaymentMethodSchema),
	billingEmail: z.string().nullable(),
	billingName: z.string().nullable(),
	billingAddress: z
		.object({
			line1: z.string().nullable(),
			line2: z.string().nullable(),
			city: z.string().nullable(),
			state: z.string().nullable(),
			postalCode: z.string().nullable(),
			country: z.string().nullable(),
		})
		.nullable(),
	// Company info stored on Stripe Customer
	companyName: z.string().nullable(),
	taxIds: z.array(TaxIdSchema),
});

// Invoices list schema
export const InvoicesListSchema = z.object({
	invoices: z.array(SubscriptionInvoiceSchema),
	hasMore: z.boolean(),
	lastInvoiceId: z.string().nullable(),
});

export const SubscriptionStatusSchema = z.enum([
	"active",
	"trialing",
	"incomplete",
	"incomplete_expired",
	"canceled",
	"past_due",
	"unpaid",
]);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

export const UserSubscriptionSchema = z.object({
	userId: z.string(),
	plan: PlanIdSchema,
	stripeSubscriptionId: z.string().nullable(),
	subscriptionStatus: SubscriptionStatusSchema,
	currentPeriodEnd: z.coerce.date(),
	createdAt: z.coerce.date(),
	updatedAt: z.coerce.date(),
});
export type UserSubscription = z.infer<typeof UserSubscriptionSchema>;

export const PlanLimitsSchema = z.object({
	apps: z.number().int(),
	links: z.number(),
	partners: z.number(),
	claims: z.number().int(),
	/**
	 * UI-only: Included payout volume in cents.
	 * Actual enforcement is via Stripe tiered metered pricing (SUM aggregation).
	 */
	payoutCreditsCents: z.number().int(),
	/** Minimum payout threshold in cents (floor for all tiers: $25) */
	minPayoutThresholdCents: z.number().int(),
	/** Maximum payout threshold in cents (varies by tier) */
	maxPayoutThresholdCents: z.number().int(),
});

/**
 * Schema for enterprise custom limits stored in the database.
 * All fields are optional - missing fields inherit from Scale defaults.
 */
export const CustomLimitsSchema = z
	.object({
		apps: z.number().int().positive().optional(),
		links: z.number().positive().optional(),
		partners: z.number().positive().optional(),
		claims: z.number().int().positive().optional(),
		/**
		 * UI-only: Included payout volume in cents.
		 * Actual enforcement is via Stripe tiered metered pricing (SUM aggregation).
		 */
		payoutCreditsCents: z.number().int().nonnegative().optional(),
		/** Minimum payout threshold in cents (floor: $25 = 2500) */
		minPayoutThresholdCents: z.number().int().min(2500).optional(),
		/** Maximum payout threshold in cents */
		maxPayoutThresholdCents: z.number().int().min(2500).optional(),
	})
	.nullable();
export type CustomLimits = z.infer<typeof CustomLimitsSchema>;

export const UsageSchema = z.object({
	apps: z.number().int().nonnegative(),
	partners: z.number().int().nonnegative(),
	links: z.number().int().nonnegative(),
	claims: z.number().int().nonnegative(),
	payoutVolumeCents: z.number().int().nonnegative(),
});
export type Usage = z.infer<typeof UsageSchema>;

export const SubscriptionWithUsageSchema = z.object({
	plan: PlanIdSchema,
	status: SubscriptionStatusSchema,
	currentPeriodEnd: z.coerce.date().nullable(),
	usage: UsageSchema,
	limits: PlanLimitsSchema,
});
export type SubscriptionWithUsage = z.infer<typeof SubscriptionWithUsageSchema>;

export const CreateCheckoutSessionInputSchema = z.object({
	plan: z.enum(["bootstrap", "standard", "scale"]),
	billingPeriod: z.enum(["monthly", "yearly"]).default("monthly"),
});

export const CreateCheckoutSessionOutputSchema = z.object({
	url: z.url(),
});

export const CreateEmbeddedCheckoutSessionOutputSchema = z.object({
	clientSecret: z.string(),
	isSetupIntent: z.boolean(),
});

export const CreatePortalSessionOutputSchema = z.object({
	url: z.url(),
});

export const CreateSetupIntentOutputSchema = z.object({
	clientSecret: z.string(),
});

export const UpdateBillingAddressInputSchema = z.object({
	name: z.string().min(1),
	line1: z.string().min(1),
	line2: z.string().optional(),
	city: z.string().min(1),
	state: z.string().optional(),
	postalCode: z.string().min(1),
	country: z.string().length(2), // ISO 3166-1 alpha-2
});

export const UpdateCompanyInfoInputSchema = z.object({
	name: z.string().min(1),
	country: z.string().length(2), // ISO 3166-1 alpha-2
	companyName: z.string().optional(),
	companyIdType: CompanyIdTypeSchema.optional(),
	companyIdValue: z.string().optional(),
});

/**
 * Creates a usage limits schema for validation.
 * Use the returned schema with .safeParse() to check if usage is within limits.
 */
export function createUsageLimitsSchema(limits: {
	apps: number;
	partners: number;
	links: number;
	claims: number;
}) {
	return z.object({
		apps: z
			.number()
			.max(limits.apps - 1, `App limit reached (max: ${limits.apps})`),
		partners: z
			.number()
			.max(
				limits.partners === Infinity
					? Number.MAX_SAFE_INTEGER
					: limits.partners - 1,
				`Partner limit reached (max: ${limits.partners === Infinity ? "unlimited" : limits.partners})`,
			),
		links: z
			.number()
			.max(
				limits.links === Infinity ? Number.MAX_SAFE_INTEGER : limits.links - 1,
				`Link limit reached (max: ${limits.links === Infinity ? "unlimited" : limits.links})`,
			),
		claims: z
			.number()
			.max(limits.claims - 1, `Claim limit reached (max: ${limits.claims})`),
	});
}
